import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";

import { ButtonThemeVariant } from "@multiweb/reactComponents";
import { bemClassNames } from "@multiweb/reactUtils";
import { WebcamUtils } from "@multiweb/reactUtils/webcam";
import { IconCode } from "@multiweb/types/contracts";
import { IconButton } from "../IconButton";

export type AvatarSnapProps = {
    /**
     * Informace o tom zda spustit stream z kamery
     */
    enabled: boolean;
    /**
     * Informace zda zobrazit tlačítko pro pořízení snímku
     */
    canSnap: boolean;
    /**
     * Callback pro stisk tlačítka na pořízení snímku
     * @param blob Blob formát souboru s pořízeným snímkem
     */
    snapButtonClick?: (blob: Blob | null) => void;
    /**
     * Ikona tlačítka pro pořízení snímku
     */
    snapButtonIcon?: IconCode;
    /**
     * Varianta tlačítka
     */
    snapButtonVariant?: ButtonThemeVariant;
};

const classNames = bemClassNames("avatar-snap");

export const AvatarSnap: FunctionComponent<AvatarSnapProps> = ({
    enabled,
    canSnap,
    snapButtonClick,
    snapButtonIcon = IconCode.toolCamera,
    snapButtonVariant,
}) => {
    const [webcam, setWebcam] = useState<WebcamUtils | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const canvasRef = useCallback(node => {
        if (videoRef?.current) {
            setWebcam(new WebcamUtils(videoRef.current, "user", node));
        }
    }, []);

    useEffect(() => {
        if (!webcam) return;

        if (enabled) {
            webcam.start();
        } else {
            webcam.stop();
        }
    }, [webcam, enabled]);

    const snapButtonCallback = () => {
        if (!webcam || !snapButtonClick || !canSnap) {
            return;
        }
        webcam.snapToBlob().then(snapButtonClick);
    };

    return (
        <div className={classNames()}>
            <div className={classNames("preview")}>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video ref={videoRef}></video>
            </div>
            <div className={classNames("toolbar")}>
                {canSnap && <IconButton variant={snapButtonVariant} iconCode={snapButtonIcon} onClick={snapButtonCallback}></IconButton>}
            </div>
            <div className={classNames("helper")}>
                <canvas ref={canvasRef}></canvas>
            </div>
        </div>
    );
};
